import { useState, useMemo, useEffect } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import { Container, Card, InputDiv, Select, TableContainer, Table, Td, Th, Tr, AddButton, ButtonDiv } from './styles'
import { Result, PageContainer, PageHeader, Badge, InfoCard, BackButton } from '../../../styles/global'
import { FaEdit, FaMinusCircle, FaPlusCircle } from 'react-icons/fa'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'

export function GeotechnicalPile() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [selectedMode, setSelectedMode] = useState('Aoki-Velloso')
  const [load, setLoad] = useState(0)
  const [area, setArea] = useState(0)
  const [perimeter, setPerimeter] = useState(0)
  const [selectedPile, setSelectedPile] = useState()
  const [nspt, setNspt] = useState(0)
  const [selectedSoil, setSelectedSoil] = useState()
  const [layers, setLayers] = useState([])
  const [result, setResult] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)

  useEffect(() => {
    if (layers.length == 0) setIsFormValid(false)
    else setIsFormValid(true)
  }, [layers])

  const soilTypesByAoki = useMemo(
    () => [
      { id: 1, type: 'Areia', k: 10, alpha: 1.4 },
      { id: 2, type: 'Areia siltosa', k: 8, alpha: 2 },
      { id: 3, type: 'Areia silto-argilosa', k: 7, alpha: 2.4 },
      { id: 4, type: 'Areia argilosa', k: 6, alpha: 3 },
      { id: 5, type: 'Areia argilo-siltosa', k: 5, alpha: 2.8 },
      { id: 6, type: 'Silte', k: 4, alpha: 3 },
      { id: 7, type: 'Silte arenoso', k: 5.5, alpha: 2.2 },
      { id: 8, type: 'Silte areno-argiloso', k: 4.5, alpha: 2.8 },
      { id: 9, type: 'Silte argiloso', k: 2.3, alpha: 3.4 },
      { id: 10, type: 'Silte argilo-arenoso', k: 2.5, alpha: 3 },
      { id: 11, type: 'Argila', k: 2, alpha: 6 },
      { id: 12, type: 'Argila arenosa', k: 3.5, alpha: 2.4 },
      { id: 13, type: 'Argila areno-siltosa', k: 3, alpha: 2.8 },
      { id: 14, type: 'Argila siltosa', k: 2.2, alpha: 4 },
      { id: 15, type: 'Argila silto-arenosa', k: 3.3, alpha: 3 },
    ],
    []
  )

  const pilesByAoki = useMemo(
    () => [
      { id: 1, type: 'Metálica', f1: 1.75, f2: 3.5 },
      { id: 2, type: 'Pré-moldada de concreto D<60cm', f1: 1.75, f2: 3.5 },
      { id: 3, type: 'Pré-moldada de concreto D>60cm', f1: 2.5, f2: 1.4 },
      { id: 4, type: 'Escavada D<60cm', f1: 3, f2: 6 },
      { id: 5, type: 'Franki', f1: 2.5, f2: 5 },
      { id: 6, type: 'Strauss', f1: 4.2, f2: 3.8 },
    ],
    []
  )

  const soilTypesByDecourt = useMemo(
    () => [
      { id: 1, type: 'Argilas', c: 1.2 },
      { id: 2, type: 'Siltes argilosos', c: 2 },
      { id: 3, type: 'Siltes arenosos', c: 2.5 },
      { id: 4, type: 'Areias', c: 4 },
    ],
    []
  )

  const pilesByDecourt = useMemo(
    () => [
      {
        id: 1,
        type: 'Cravada',
        soils: [
          { type: 'Argilas', alpha: 1, beta: 1 },
          { type: 'Siltes', alpha: 1, beta: 1 },
          { type: 'Areias', alpha: 1, beta: 1 },
        ],
      },
      {
        id: 2,
        type: 'Escavada (em geral)',
        soils: [
          { type: 'Argilas', alpha: 0.85, beta: 0.8 },
          { type: 'Siltes', alpha: 0.6, beta: 0.65 },
          { type: 'Areias', alpha: 0.5, beta: 0.5 },
        ],
      },
      {
        id: 3,
        type: 'Escavada (bentonita)',
        soils: [
          { type: 'Argilas', alpha: 0.85, beta: 0.9 },
          { type: 'Siltes', alpha: 0.6, beta: 0.75 },
          { type: 'Areias', alpha: 0.5, beta: 0.6 },
        ],
      },
      {
        id: 4,
        type: 'Hélice contínua',
        soils: [
          { type: 'Argilas', alpha: 0.3, beta: 1 },
          { type: 'Siltes', alpha: 0.3, beta: 1 },
          { type: 'Areias', alpha: 0.3, beta: 1 },
        ],
      },
      {
        id: 5,
        type: 'Raiz',
        soils: [
          { type: 'Argilas', alpha: 0.85, beta: 1.5 },
          { type: 'Siltes', alpha: 0.6, beta: 1.5 },
          { type: 'Areias', alpha: 0.5, beta: 1.5 },
        ],
      },
      {
        id: 6,
        type: 'Injetada',
        soils: [
          { type: 'Argilas', alpha: 1, beta: 3 },
          { type: 'Siltes', alpha: 1, beta: 3 },
          { type: 'Areias', alpha: 1, beta: 3 },
        ],
      },
    ],
    []
  )

  useEffect(() => {
    setSelectedSoil(soilTypesByAoki[0].type)
    setSelectedPile(pilesByAoki[0].type)
  }, [pilesByAoki, soilTypesByAoki])

  const modes = useMemo(() => ['Aoki-Velloso', 'Décourt-Quaresma'], [])

  function handleAddLayer(event) {
    event.preventDefault()

    setLayers([...layers, { depth: layers.length + 1, nspt, soil: selectedSoil }])

    setNspt(0)
  }

  function handleRemoveLayer(event, index) {
    event.preventDefault()
    setLayers((prevLayers) => {
      const newLayers = prevLayers.filter((_, i) => i !== index)

      return newLayers.map((layer, i) => ({ ...layer, depth: i + 1 }))
    })
  }

  function handleEditLayer(event, index) {
    event.preventDefault()
    setLayers((prevLayers) =>
      prevLayers.map((layer, i) => (i === index ? { ...layer, nspt, soil: selectedSoil } : layer))
    )
  }

  function handleChangeMode(event) {
    event.preventDefault()

    const mode = event.target.value

    setSelectedMode(mode)

    if (mode == 'Aoki-Velloso') {
      setSelectedSoil(soilTypesByAoki[0].type)
      setSelectedPile(pilesByAoki[0].type)
    } else {
      setSelectedSoil(soilTypesByDecourt[0].type)
      setSelectedPile(pilesByDecourt[0].type)
    }

    setLayers(layers.map((l) => ({ ...l, soil: '' })))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    try {
      const pile = pilesByAoki.find((p) => p.type == selectedPile)

      const data = {
        selectedMode,
        pile,
        perimeter,
        area,
        layers: layers.map((l) => ({
          ...l,
          ...(selectedMode == 'Aoki-Velloso'
            ? {
                k: soilTypesByAoki.find((s) => s.type == l.soil).k,
                alpha: soilTypesByAoki.find((s) => s.type == l.soil).alpha,
              }
            : {
                alpha: pilesByDecourt.find((p) => p.type == selectedPile).soils.find((s) => l.soil.includes(s.type))
                  .alpha,
                beta: pilesByDecourt.find((p) => p.type == selectedPile).soils.find((s) => l.soil.includes(s.type))
                  .beta,
                c: soilTypesByDecourt.find((s) => s.type == l.soil).c,
              }),
        })),
      }

      const response = await api.post('/geotechnical/pile', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    }
  }

  const headerGradient = 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'

  return (
    <PageContainer>
      <PageHeader $gradient={headerGradient}>
        <div className="header-content">
          <BackButton to="/geotecnia">
            <FiArrowLeft /> Voltar para Geotecnia
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/geotecnia">Geotecnia</a>
            <span>/</span>
            <span>Capacidade de Estacas</span>
          </div>
          <h1>Capacidade de Carga de Estacas</h1>
          <p className="subtitle">
            Dimensionamento pelos métodos Aoki-Velloso e Décourt-Quaresma, considerando os dados do solo e da estaca.
          </p>
          <div className="badges">
            <Badge>NBR 6122:2019</Badge>
            <Badge>Aoki-Velloso e Décourt-Quaresma</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e9f7ef 0%, #d1fae5 100%)"
        $border="2px solid #34d399"
        $primaryColor="#15803d"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Estime a capacidade de carga de estacas com base nos métodos Aoki-Velloso e Décourt-Quaresma informando
              geometria, perímetro, área e valores de NSPT por camada.
            </p>
            <p>
              Os resultados apresentam a contribuição de ponta e lateral em cada profundidade, além da carga admissível
              total.
            </p>
            <div className="norms">
              <span>NBR 6122:2019</span>
              <span>Aoki & Velloso</span>
              <span>Décourt & Quaresma</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <h2>Capacidade de carga</h2>
          <h3>Dados gerais</h3>
          <InputDiv>
            <span>Método de dimensionamento</span>
            <div>
              <Select id="modeSelect" value={selectedMode} onChange={(event) => handleChangeMode(event)}>
                {modes.map((s, index) => (
                  <option key={index} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>
          </InputDiv>
          <InputDiv>
            <span>Carga (kN)</span>
            <input type="number" value={load} onChange={(event) => setLoad(event.target.value)} />
          </InputDiv>
          <InputDiv>
            <span>Tipo de estaca</span>
            <div>
              <Select id="pileSelect" value={selectedPile} onChange={(event) => setSelectedPile(event.target.value)}>
                {selectedMode == 'Aoki-Velloso'
                  ? pilesByAoki.map((p) => (
                      <option key={p.id} value={p.type}>
                        {p.type}
                      </option>
                    ))
                  : pilesByDecourt.map((p) => (
                      <option key={p.id} value={p.type}>
                        {p.type}
                      </option>
                    ))}
              </Select>
            </div>
          </InputDiv>
          <InputDiv>
            <span>Área da seção da estaca (cm²)</span>
            <input type="number" value={area} onChange={(event) => setArea(event.target.value)} />
          </InputDiv>
          <InputDiv>
            <span>Perímetro da seção da estaca (cm)</span>
            <input type="number" value={perimeter} onChange={(event) => setPerimeter(event.target.value)} />
          </InputDiv>
          <h3>Inserção das camadas</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>
                Valor do N<sub>SPT</sub>
              </span>
              <div>
                <input type="number" value={nspt == 0 ? '' : nspt} onChange={(event) => setNspt(event.target.value)} />
                <AddButton type="submit" disabled={false} onClick={handleAddLayer}>
                  <FaPlusCircle />
                </AddButton>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Tipo de solo</span>
              <div>
                <Select id="soilSelect" value={selectedSoil} onChange={(event) => setSelectedSoil(event.target.value)}>
                  {selectedMode == 'Aoki-Velloso'
                    ? soilTypesByAoki.map((s) => (
                        <option key={s.id} value={s.type}>
                          {s.type}
                        </option>
                      ))
                    : soilTypesByDecourt.map((s) => (
                        <option key={s.id} value={s.type}>
                          {s.type}
                        </option>
                      ))}
                </Select>
              </div>
            </InputDiv>
            <h3>Camadas inseridas</h3>
            {layers.length < 1 ? (
              <p>Nenhuma camada inserida.</p>
            ) : (
              <TableContainer>
                <Table>
                  <thead>
                    <Tr>
                      <Th>Profundidade (m)</Th>
                      <Th>
                        N<sub>SPT</sub>
                      </Th>
                      <Th>Solo</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {layers.map((l, index) => (
                      <Tr key={index}>
                        <Td>{l.depth}</Td>
                        <Td>{l.nspt}</Td>
                        <Td>{l.soil}</Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button onClick={(event) => handleRemoveLayer(event, index)}>
                            <FaMinusCircle />
                          </button>
                          <button style={{ marginLeft: '20px' }} onClick={(event) => handleEditLayer(event, index)}>
                            <FaEdit />
                          </button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}
            <ButtonDiv $isFormValid={isFormValid}>
              <button type="submit" disabled={!isFormValid || !canCalculate}>
                {!canCalculate ? 'Limite atingido' : 'Calcular'}
              </button>
            </ButtonDiv>
          </form>
        </Card>
        {result !== '' && (
          <Result>
            <h2>Resultado</h2>
            <TableContainer>
              <Table>
                <thead>
                  <Tr>
                    <Th>Profundidade (m)</Th>
                    <Th>Carga de ponta (kN)</Th>
                    <Th>Carga lateral (kN)</Th>
                    <Th>Carga lateral acumulada (kN)</Th>
                    <Th>Carga total (kN)</Th>
                    <Th>Carga admissível (kN)</Th>
                  </Tr>
                </thead>
                <tbody>
                  {result.map((l) => (
                    <Tr key={l.depth}>
                      <Td>{l.depth}</Td>
                      <Td>{l.endForce}</Td>
                      <Td>{l.lateralForce}</Td>
                      <Td>{l.lateralForces}</Td>
                      <Td>{l.totalForces}</Td>
                      <Td>{l.designForce}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </Result>
        )}
      </Container>
    </PageContainer>
  )
}
